import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { IconButton } from '@atlaskit/button/new';
import Textfield from '@atlaskit/textfield';
import { Dimensions } from 'components/visuals';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';
import Button, { LoadingButton } from '@atlaskit/button';
import {
  editLicenseNameInline,
  restoreEditLicenseNameInline,
} from 'modules/mac/duck/actions/accountId';
import {
  selectEditedLicenseNameInline,
  selectEditedLicenseNameInlineResult,
} from 'modules/mac/duck/selectors';
import { licensesPreselected } from 'modules/mac/duck/actions';
import { Action, redirect } from 'redux-first-router';
import { colors } from '@atlaskit/theme';
import { BTFProductTypes } from 'util/mac/helpers';

const EditIconComponent = () => (
  <EditFilledIcon label="mac.orders.table.table-item.action.edit" size="small" />
);

const StyledTextfield = styled(Textfield)`
  display: inline-block !important;
  margin-right: ${Dimensions.units(1)}px;
`;

const StyledErrorText = styled.span`
  color: ${colors.R400};
`;

const NameEditableComponent = ({ accountId, accountName, productType }) => {
  const dispatch = useDispatch();

  const editedLicenseName = useSelector(selectEditedLicenseNameInline);
  const editedLicenseNameResult = useSelector(selectEditedLicenseNameInlineResult);

  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isError, setIsError] = useState(false);
  const [updatedName, setUpdatedName] = useState(accountName);

  const shouldShowEditIcon = BTFProductTypes.includes(productType);

  useEffect(() => {
    if (editedLicenseNameResult === '') {
      dispatch(restoreEditLicenseNameInline());
      dispatch(
        redirect(licensesPreselected.licensePreselectedRoute(accountId.toString(), {}) as Action),
      );
    } else if (editedLicenseNameResult) {
      setIsError(true);
    }
  }, [editedLicenseNameResult]);

  const handleNameInputChange = (e) => {
    setUpdatedName(e.target.value);
  };

  const onSave = () => {
    setIsError(false);
    if (updatedName.trim()) {
      dispatch(editLicenseNameInline(accountId, updatedName));
    }
  };

  const onCancel = () => {
    setIsInEditMode(false);
    setUpdatedName(accountName);
  };

  return isInEditMode ? (
    <>
      <div>
        <StyledTextfield
          name="name"
          onChange={handleNameInputChange}
          data-cy="mac-licenses-edit-name-input"
          width={Dimensions.units(40)}
          isRequired
          isCompact
          value={updatedName || ''}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave();
            }
          }}
        />
        <LoadingButton
          onClick={onSave}
          appearance="primary"
          isLoading={editedLicenseName.inProgress}
        >
          <FormattedMessage defaultMessage="Save" id="mac.licenses.edit-name.save" />
        </LoadingButton>{' '}
        <Button onClick={onCancel} appearance="subtle">
          <FormattedMessage defaultMessage="Cancel" id="mac.licenses.edit-name.cancel" />
        </Button>
      </div>
      {isError && (
        <StyledErrorText>
          <FormattedMessage
            defaultMessage="Something went wrong. Please try again."
            id="mac.licenses.edit-name.error"
          />
        </StyledErrorText>
      )}
    </>
  ) : (
    <>
      {accountName}
      {shouldShowEditIcon && (
        <IconButton
          onClick={() => setIsInEditMode(true)}
          appearance="subtle"
          spacing="compact"
          label="edit name"
          icon={EditIconComponent}
          testId={`licenses-table-edit-name-icon-${accountId}`}
        />
      )}
    </>
  );
};

export default NameEditableComponent;
